// Helpers for worksheet functionality common to multiple domains
// (e.g. individual worksheet editor + worksheet grid)

import { calculateChangeTracking } from 'Data/Core/ChangeTracker';
import { IDataSyncState } from 'Data/ReduxViewModels/DataSync/types';
import { IWorksheetEditorStatus } from 'Data/ReduxViewModels/WorksheetEditor/types';
import { getConflicts, isMetaDataProperty } from 'Data/ViewModels/IApiDataModel';
import { IDataSyncModel } from 'Data/ViewModels/IDataSyncModel';
import { hasNonEmptyValue } from 'Lib/Any';
import * as ioPath from 'Lib/Object/Path/Immutable';
import { joinPaths } from 'Lib/Object/Path/shared';
import { union } from 'ramda';

// Determines calculated status properties to patch onto current status.
export const getWorksheetStatusPatch = (worksheet: any, user: any, currentStatus: any) => {
  const {
    header: { IsChecked, IsOnSignedReport, CompletedAtDifferentLab },
    type: { Id: worksheetTypeId, IsRecord: worksheetTypeIsRecord },
  } = worksheet;

  const worksheetParentTypeId = worksheet.header.ParentTest?.WorksheetType?.Id;

  const { synchronising, importing, settingTestedByCheckedBy, deleting } = currentStatus;

  const {
    data: {
      Claims: {
        'sq.worksheets.edit': worksheetsEditableClaim = undefined,
        'sq.laboratory.supervisor': testedCheckedByUncheckable = undefined,
      } = {},
    },
  } = user;

  let statusPatch = getDefaultWorksheetStatusPatch();

  statusPatch.saving = synchronising || importing || settingTestedByCheckedBy;

  const editableWorksheetTypeIds = worksheetsEditableClaim ? worksheetsEditableClaim.split(',') : [];

  //Lock all fields if worksheet has been completed in a different laboratory
  if (!!CompletedAtDifferentLab) {
    statusPatch.readonly.locked = true;
    statusPatch.readonly.uncheckAllowed = false;
    statusPatch.readonly.lockedMessage = 'The worksheet is read only: it has been completed in a different laboratory.';
  }
  // Lock all fields on worksheet if they do not have "edit" claim for worksheet in question
  else if (!editableWorksheetTypeIds.includes(worksheetTypeIsRecord ? worksheetParentTypeId : worksheetTypeId)) {
    statusPatch.readonly.locked = true;
    statusPatch.readonly.lockedMessage = 'The worksheet is read only: edit permissions are required to make changes.';
  }
  // Lock all fields on worksheet if it has been signed.
  else if (IsOnSignedReport) {
    statusPatch.readonly.locked = true;
    statusPatch.readonly.lockedMessage =
      'The worksheet is read only: the results have been included on a signed test report.';
  }
  // Lock all fields if worksheet has been checked
  else if (!!IsChecked) {
    statusPatch.readonly.locked = true;
    statusPatch.readonly.uncheckAllowed = !!testedCheckedByUncheckable;
    statusPatch.readonly.lockedMessage = statusPatch.readonly.uncheckAllowed
      ? 'The worksheet is read only: it has been checked. You can clear the checked status if necessary.'
      : 'The worksheet is read only: it has been checked. A laboratory supervisor can clear the checked status if necessary.';
  }
  // Lock all fields is worksheet is currently being deleted
  else if (!!deleting) {
    statusPatch.readonly.locked = true;
    statusPatch.readonly.lockedMessage = 'The worksheet is currently being deleted and cannot be changed.';
  }

  return statusPatch;
};

export const getDefaultWorksheetStatusPatch = (): Pick<IWorksheetEditorStatus, 'saving' | 'readonly'> => ({
  saving: false,
  readonly: { locked: false, uncheckAllowed: false, lockedMessage: '' },
});

type ConflictData = {
  syncConflictUsers: IDataSyncModel['syncConflictUsers'];
  syncConflictFields: {
    [fieldPath: string]: {
      clientValue: string;
      serverValue: string;
    };
  };
};

/**
 * Returns data about the sync conflicts present on the given syncable state.
 * Used to inform the user when deciding how to resolve the conflict.
 */
export const getConflictData = (syncState: IDataSyncState): ConflictData => {
  const {
    syncModel_conflictClientBefore: clientModelBefore,
    syncModel: clientModelCurrent,
    syncModel_synced: serverModel,
  } = syncState;

  // Get paths with conflicts flagged by the server
  const serverDataConflictPaths = getConflicts(serverModel.data)
    .flatMap((conflicts: any) => conflicts.value.map((field: any) => joinPaths(conflicts.path, field)))
    .flatMap((path) => {
      // Drill down into the models for individual properties with changes
      const client = ioPath.get(clientModelCurrent.data, path);
      const server = ioPath.get(serverModel.data, path);

      if (typeof client !== 'object' && typeof server !== 'object') {
        return [path];
      }

      return Object.keys(calculateChangeTracking(server, client).changeTracking).map((k) => joinPaths(path, k));
    });

  // Get changes that have been made since the last client model was sent
  // (will be lost if client changes are discarded)
  const clientDataChangePaths = Object.keys(
    calculateChangeTracking(clientModelBefore?.data, clientModelCurrent.data).changeTracking
  );

  /**
   * Given a path on the data model with a conflict detected,
   * create a path from the model base to the "field" level
   * (where config properties such as labels will be defined)
   */
  const toFieldPath = (path: string) => joinPaths('data', path.replace(/\.value$/, ''));

  /**
   * Produces a readable string version of a value to present for conflict resolution
   * Note this is different from the formated value that can appear in a text output field
   */
  const toReadableValue = (path: string, value: any): string => {
    let readable: string;

    if (Array.isArray(value) && value.length > 0) {
      // Support displaying simple arrays of values that can be shown as text without much effort
      readable = `[${value.map((item) => (item?.value !== undefined ? item.value : item)).join(', ')}]`;
    } else {
      if (value?.value !== undefined) value = value.value;
      readable = value?.toString();
    }

    if (readable) {
      if (readable.includes('[object')) readable = '(multiple)';
    } else {
      // If path ends with [*] then it's probably a deleted array item, not a cleared value
      if (/\[.*\]$/.test(path)) readable = '(deleted)';
    }

    return readable;
  };

  const syncConflictFields = Object.fromEntries(
    union(serverDataConflictPaths, clientDataChangePaths)
      // Sanitise meta data properties (Messages, Limits, etc.)
      .filter((path) => !ioPath.parse(path).some(isMetaDataProperty))
      .map((path) => [
        toFieldPath(path),
        {
          clientValue: toReadableValue(path, ioPath.get(clientModelCurrent.data, path)),
          serverValue: toReadableValue(path, ioPath.get(serverModel.data, path)),
        },
      ])
  );

  const syncConflictUsers =
    serverModel.syncConflictUsers ?? (hasNonEmptyValue(syncConflictFields) ? [{ Name: 'Unknown', Timestamp: '' }] : []);

  return {
    syncConflictUsers,
    syncConflictFields,
  };
};

/**
 * Returns a user friendly title for a given worksheet model
 */
export const getWorksheetTitle = (worksheet: any) =>
  `${worksheet.header?.Sample?.SampleId} ${
    worksheet.header?.TopLevelTest?.WorksheetType?.ShortName ?? worksheet.type?.ShortName
  }`;
